/**
 * Escolha da nova senha, a partir do link recebido por e-mail.
 *
 * O endereço é `/redefinir-senha?token=…` — o mesmo que o backend monta em
 * `send_password_reset`. Mudar um dos dois quebra o outro em silêncio.
 *
 * O token é de uso único e expira. Quando ele não serve mais, o backend
 * responde com erro e a tela oferece o caminho de volta: pedir outro link. Sem
 * isso, quem clicou num e-mail antigo ficaria numa tela morta.
 */

import { useState } from "react";
import { Logo } from "../ds";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import CampoSenha from "../components/CampoSenha";
import { ApiError, apiFetch } from "../api/client";

/** Espelha `new_password: Field(min_length=10)` em `app/schemas/auth.py`. */
const TAMANHO_MINIMO = 10;

export default function RedefinirSenhaPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [tokenInvalido, setTokenInvalido] = useState(false);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();

    if (senha !== confirmacao) {
      setErro("A confirmação não confere com a nova senha.");
      return;
    }
    if (senha.length < TAMANHO_MINIMO) {
      setErro(`A senha precisa ter pelo menos ${TAMANHO_MINIMO} caracteres.`);
      return;
    }

    setEnviando(true);
    setErro(null);

    try {
      await apiFetch("/auth/password/reset", {
        method: "POST",
        body: { token, new_password: senha },
      });
      // Redefinir encerra as sessões abertas; entrar de novo é o caminho honesto.
      navigate("/entrar", { replace: true, state: { senhaTrocada: true } });
    } catch (falha) {
      if (falha instanceof ApiError && (falha.status === 400 || falha.status === 404)) {
        setTokenInvalido(true);
      } else {
        setErro(
          falha instanceof ApiError
            ? falha.message
            : "Não foi possível redefinir a senha. Tente de novo.",
        );
      }
    } finally {
      setEnviando(false);
    }
  }

  if (!token || tokenInvalido) {
    return (
      <div className="shell">
        <div className="brand">
          <Logo tamanho={32} />
          <b>Aletia</b>
          <span>Armaturen · Valve Passport</span>
        </div>

        <div className="card card-estreito">
          <h1>Link inválido ou expirado</h1>
          <p className="sub">
            {token
              ? "Este link já foi usado ou passou da validade. Cada link serve uma vez só."
              : "O endereço veio sem o código de redefinição. Abra o link direto do e-mail."}
          </p>
          <div className="acoes">
            <Link className="btn btn-primario" to="/esqueci-senha">
              Pedir um novo link
            </Link>
            <Link className="btn" to="/entrar">
              Voltar para o login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shell">
      <div className="brand">
        <Logo tamanho={32} />
        <b>Aletia</b>
        <span>Armaturen · Valve Passport</span>
      </div>

      <form className="card card-estreito" onSubmit={enviar}>
        <h1>Escolha uma nova senha</h1>
        <p className="sub">
          Ao confirmar, as sessões abertas nesta conta são encerradas — inclusive em outros
          aparelhos.
        </p>

        <CampoSenha
          rotulo="Nova senha"
          valor={senha}
          onChange={setSenha}
          autoComplete="new-password"
          minLength={TAMANHO_MINIMO}
          autoFocus
          ajuda={`Pelo menos ${TAMANHO_MINIMO} caracteres.`}
        />

        <CampoSenha
          rotulo="Confirme a nova senha"
          valor={confirmacao}
          onChange={setConfirmacao}
          autoComplete="new-password"
          minLength={TAMANHO_MINIMO}
        />

        {erro && (
          <p className="alerta" role="alert">
            {erro}
          </p>
        )}

        <button type="submit" className="btn btn-primario" disabled={enviando}>
          {enviando ? "Redefinindo…" : "Redefinir senha"}
        </button>
      </form>
    </div>
  );
}
